import React, { useState, useEffect } from 'react';
import { Star, StarBorder, StarHalf } from '@mui/icons-material';
import welfareService from '../utils/welfareService';
import { snackbar } from '../utils/showMessage';

const RatingSection = ({ serviceId }) => {
    const [ratings, setRatings] = useState([]);
    const [averageRating, setAverageRating] = useState({ average: 0, count: 0 });
    const [userRating, setUserRating] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ rating: 0, comment: '' });
    const [hoveredRating, setHoveredRating] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadRatings();
        loadAverageRating();
        loadUserRating();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceId]);

    const loadRatings = async () => {
        try {
            const data = await welfareService.getServiceRatings(serviceId);
            setRatings(data);
        } catch (error) {
            console.error('Error loading ratings:', error);
        }
    };

    const loadAverageRating = async () => {
        try {
            const data = await welfareService.getServiceAverageRating(serviceId);
            setAverageRating(data);
        } catch (error) {
            console.error('Error loading average rating:', error);
        }
    };

    const loadUserRating = async () => {
        try {
            const data = await welfareService.getUserRating(serviceId);
            setUserRating(data);
            if (data) {
                setFormData({ rating: data.rating, comment: data.comment || '' });
            }
        } catch (error) {
            // User not logged in or hasn't rated yet
            setUserRating(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.rating === 0) {
            snackbar('لطفاً امتیاز خود را انتخاب کنید');
            return;
        }

        setLoading(true);
        try {
            if (userRating) {
                await welfareService.updateRating(userRating._id, formData);
                snackbar('امتیاز شما به‌روزرسانی شد');
            } else {
                await welfareService.createRating({ ...formData, serviceId });
                snackbar('امتیاز شما ثبت شد');
            }
            setShowForm(false);
            await Promise.all([loadRatings(), loadAverageRating(), loadUserRating()]);
        } catch (error) {
            console.error('Error submitting rating:', error);
            snackbar(error.response?.data?.message || 'خطا در ثبت امتیاز');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('آیا از حذف امتیاز خود مطمئن هستید؟')) return;

        setLoading(true);
        try {
            await welfareService.deleteRating(userRating._id);
            snackbar('امتیاز شما حذف شد');
            setUserRating(null);
            setFormData({ rating: 0, comment: '' });
            setShowForm(false);
            await Promise.all([loadRatings(), loadAverageRating()]);
        } catch (error) {
            console.error('Error deleting rating:', error);
            snackbar('خطا در حذف امتیاز');
        } finally {
            setLoading(false);
        }
    };

    const renderStars = (rating, interactive = false, size = 'medium') => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const iconSize = size === 'large' ? { fontSize: 40 } : size === 'small' ? { fontSize: 20 } : { fontSize: 24 };

        for (let i = 1; i <= 5; i++) {
            const filled = interactive ? (hoveredRating || formData.rating) >= i : i <= fullStars;
            const half = !interactive && i === fullStars + 1 && hasHalfStar;

            stars.push(
                <span
                    key={i}
                    onClick={interactive ? () => setFormData({ ...formData, rating: i }) : undefined}
                    onMouseEnter={interactive ? () => setHoveredRating(i) : undefined}
                    onMouseLeave={interactive ? () => setHoveredRating(0) : undefined}
                    style={{ cursor: interactive ? 'pointer' : 'default', color: '#ffc107' }}
                >
                    {half ? <StarHalf style={iconSize} /> : filled ? <Star style={iconSize} /> : <StarBorder style={iconSize} />}
                </span>
            );
        }
        return stars;
    };

    return (
        <div className="rating-section">
            {/* Average Rating Display */}
            {!showForm ? <div className="row mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center">
                            <h6 className="mb-2">امتیاز کاربران</h6>
                            <div className="mb-2">
                                {renderStars(averageRating.average, false, 'large')}
                            </div>
                            <h4 className="mb-1">{averageRating.average.toFixed(1)} از 5</h4>
                            <p className="text-muted mb-0">بر اساس {averageRating.count} نظر</p>
                        </div>
                    </div>
                </div>
            </div> : null}

            {/* User Rating Form */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {!showForm && !userRating && (
                                <button
                                    className="btn btn-primary w-100"
                                    onClick={() => setShowForm(true)}
                                >
                                    ثبت نظر و امتیاز
                                </button>
                            )}

                            {!showForm && userRating && (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">نظر شما</h6>
                                        <div>
                                            <button
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={() => setShowForm(true)}
                                            >
                                                ویرایش
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={handleDelete}
                                            >
                                                حذف
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mb-2">{renderStars(userRating.rating)}</div>
                                    {userRating.comment && <p className="mb-0">{userRating.comment}</p>}
                                </div>
                            )}

                            {showForm && (
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label">امتیاز شما (از 1 تا 5)</label>
                                        <div className="d-flex justify-content-center mb-2">
                                            {renderStars(formData.rating, true, 'large')}
                                        </div>
                                        <div className="text-center">
                                            <span className="badge bg-primary">
                                                {hoveredRating || formData.rating || 'انتخاب کنید'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">نظر شما (اختیاری)</label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={formData.comment}
                                            onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                                            placeholder="نظر خود را درباره این خدمت بنویسید..."
                                        />
                                    </div>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="submit"
                                            className="btn btn-primary flex-grow-1"
                                            disabled={loading}
                                        >
                                            {loading ? 'در حال ثبت...' : userRating ? 'به‌روزرسانی' : 'ثبت نظر'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setShowForm(false);
                                                if (userRating) {
                                                    setFormData({ rating: userRating.rating, comment: userRating.comment || '' });
                                                } else {
                                                    setFormData({ rating: 0, comment: '' });
                                                }
                                            }}
                                            disabled={loading}
                                        >
                                            انصراف
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* All Ratings List */}
            {ratings.length > 0 && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <h6 className="mb-3">نظرات کاربران ({ratings.length})</h6>
                                {ratings.map((rating) => (
                                    <div key={rating._id} className="border-bottom pb-3 mb-3 last-child-no-border">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <strong>
                                                    {rating.userId?.firstName} {rating.userId?.lastName}
                                                </strong>
                                                <div className="mt-1">{renderStars(rating.rating, false, 'small')}</div>
                                            </div>
                                            <small className="text-muted">
                                                {new Date(rating.createdAt).toLocaleDateString('fa-IR')}
                                            </small>
                                        </div>
                                        {rating.comment && <p className="mb-0 text-justify">{rating.comment}</p>}
                                        {rating.adminReply && (
                                            <div
                                                className="mt-2 p-2 rounded"
                                                style={{ background: '#eef5fb', borderRight: '3px solid var(--bs-primary, #0d6efd)' }}
                                            >
                                                <div className="d-flex align-items-center gap-1 mb-1">
                                                    <small className="fw-bold text-primary">پاسخ نظام پزشکی</small>
                                                    {rating.adminReplyAt && (
                                                        <small className="text-muted ms-auto">
                                                            {new Date(rating.adminReplyAt).toLocaleDateString('fa-IR')}
                                                        </small>
                                                    )}
                                                </div>
                                                <p className="mb-0 small text-justify">{rating.adminReply}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RatingSection;
